import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Temporal } from "@js-temporal/polyfill";
import { dateLabel } from "../../shared/domain";
import { AppointmentIndicator } from "./AppointmentIndicator";
import { Modal } from "./ui";
const months = Array.from({ length: 12 }, (_, i) =>
  dateLabel(`2000-${String(i + 1).padStart(2, "0")}-01`, { month: "long" }),
);
export function CalendarPicker({
  value,
  today,
  appointmentDays,
  onSelect,
  onClose,
}: {
  value: string;
  today: string;
  appointmentDays: ReadonlySet<string>;
  onSelect: (date: string) => void;
  onClose: () => void;
}) {
  const initial = Temporal.PlainDate.from(value);
  const [month, setMonth] = useState(initial.with({ day: 1 })),
    [focused, setFocused] = useState(value);
  const grid = useRef<HTMLDivElement>(null);
  const focusGrid = useRef(true);
  useEffect(() => {
    if (!focusGrid.current) return;
    focusGrid.current = false;
    grid.current
      ?.querySelector<HTMLButtonElement>(`[data-date="${focused}"]`)
      ?.focus();
  }, [focused]);
  const first = month.subtract({ days: month.dayOfWeek - 1 });
  const cells = Array.from({ length: 42 }, (_, i) => first.add({ days: i }));
  function show(next: Temporal.PlainDate, moveFocus = false) {
    focusGrid.current = moveFocus;
    if (next.year < 1 || next.year > 9999) return;
    setMonth(next.with({ day: 1 }));
    setFocused(next.toString());
  }
  function keyboard(e: React.KeyboardEvent, date: string) {
    let next: Temporal.PlainDate | undefined;
    const current = Temporal.PlainDate.from(date);
    switch (e.key) {
      case "ArrowLeft":
        next = current.subtract({ days: 1 });
        break;
      case "ArrowRight":
        next = current.add({ days: 1 });
        break;
      case "ArrowUp":
        next = current.subtract({ days: 7 });
        break;
      case "ArrowDown":
        next = current.add({ days: 7 });
        break;
      case "Home":
        next = current.subtract({ days: current.dayOfWeek - 1 });
        break;
      case "End":
        next = current.add({ days: 7 - current.dayOfWeek });
        break;
      case "PageUp":
        next = current.subtract(e.shiftKey ? { years: 1 } : { months: 1 });
        break;
      case "PageDown":
        next = current.add(e.shiftKey ? { years: 1 } : { months: 1 });
        break;
      default:
        return;
    }
    e.preventDefault();
    show(next, true);
  }
  return (
    <Modal title="Choose date" onClose={onClose}>
      <div className="calendar-picker">
        <p className="muted calendar-instructions" id="calendar-help">
          Choose a day. Use the month and year fields to jump further.
        </p>
        <div className="picker-toolbar">
          <button
            className="icon-button"
            aria-label="Previous month"
            disabled={month.year === 1 && month.month === 1}
            onClick={() => show(month.subtract({ months: 1 }))}
          >
            <ChevronLeft size={20} />
          </button>
          <label className="sr-only" htmlFor="picker-month">
            Month
          </label>
          <select
            id="picker-month"
            value={month.month}
            onChange={(e) =>
              show(month.with({ month: Number(e.target.value) }))
            }
          >
            {months.map((name, i) => (
              <option key={name} value={i + 1}>
                {name}
              </option>
            ))}
          </select>
          <label className="sr-only" htmlFor="picker-year">
            Year
          </label>
          <input
            id="picker-year"
            aria-label="Year"
            type="number"
            min="1"
            max="9999"
            value={month.year}
            onChange={(e) => {
              const year = Number(e.target.value);
              if (year >= 1 && year <= 9999) show(month.with({ year }));
            }}
          />
          <button
            className="icon-button"
            aria-label="Next month"
            disabled={month.year === 9999 && month.month === 12}
            onClick={() => show(month.add({ months: 1 }))}
          >
            <ChevronRight size={20} />
          </button>
        </div>
        <h3 className="sr-only" aria-live="polite">
          {months[month.month - 1]} {month.year}
        </h3>
        <div
          className="calendar-grid"
          role="grid"
          aria-label={`${months[month.month - 1]} ${month.year}`}
          aria-describedby="calendar-help"
          ref={grid}
        >
          <div role="row" className="calendar-weekdays">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
              <span role="columnheader" key={day}>
                {day}
              </span>
            ))}
          </div>
          {Array.from({ length: 6 }, (_, row) => (
            <div role="row" className="calendar-grid-row" key={row}>
              {cells.slice(row * 7, row * 7 + 7).map((date) => {
                const key = date.toString();
                return (
                  <div role="gridcell" key={key} aria-selected={key === value}>
                    <button
                      type="button"
                      data-date={key}
                      aria-label={dateLabel(key, {
                        weekday: "long",
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                      aria-describedby={
                        appointmentDays.has(key)
                          ? "calendar-appointment-key"
                          : undefined
                      }
                      aria-current={key === today ? "date" : undefined}
                      tabIndex={key === focused ? 0 : -1}
                      className={[
                        key === value ? "selected" : "",
                        key === today ? "is-today" : "",
                        date.month !== month.month ? "outside-month" : "",
                      ].join(" ")}
                      onKeyDown={(e) => keyboard(e, key)}
                      onFocus={() => setFocused(key)}
                      onClick={() => {
                        onSelect(key);
                        onClose();
                      }}
                    >
                      {date.day}
                      <AppointmentIndicator
                        hasAppointments={appointmentDays.has(key)}
                      />
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <span id="calendar-appointment-key" className="sr-only">
          Has appointments
        </span>
        <p className="picker-selection">
          Selected ·{" "}
          {dateLabel(value, { month: "long", day: "numeric", year: "numeric" })}
        </p>
      </div>
    </Modal>
  );
}
